// ============================================
// Vocabulary Data - Re-exports with Types
// ============================================
//
// This file re-exports the existing vocabulary data from /assets/js/core/
// with proper TypeScript types applied.
//
// Migration strategy: Keep existing data files to avoid data entry errors,
// import them here with type casting for the new Preact app.

import type { Word, Particle, GrammarPattern, JLPTLevel, LevelConfig } from '@/types';

// ============================================
// Dynamic Level Loaders (for code splitting)
// ============================================

export async function loadVocabularyN5(): Promise<Word[]> {
  const module = await import('../../assets/js/core/vocabularyN5.js');
  return module.N5_VOCABULARY as Word[];
}

export async function loadVocabularyN4(): Promise<Word[]> {
  const module = await import('../../assets/js/core/vocabularyN4.js');
  return module.N4_VOCABULARY as Word[];
}

export async function loadVocabularyN3(): Promise<Word[]> {
  const module = await import('../../assets/js/core/vocabularyN3.js');
  return module.N3_VOCABULARY as Word[];
}

export async function loadVocabularyEveryday(): Promise<Word[]> {
  const module = await import('../../assets/js/core/vocabularyEveryday.js');
  return module.vocabularyData as Word[];
}

export async function loadHiragana(): Promise<Word[]> {
  const module = await import('./hiragana');
  return module.HIRAGANA_VOCABULARY;
}

export async function loadKatakana(): Promise<Word[]> {
  const module = await import('./katakana');
  return module.KATAKANA_VOCABULARY;
}

export async function loadParticlesN5(): Promise<Particle[]> {
  const module = await import('../../assets/js/core/particleDataN5.js');
  return module.particleDataN5 as unknown as Particle[];
}

export async function loadParticlesN4(): Promise<Particle[]> {
  const module = await import('../../assets/js/core/particleDataN4.js');
  return module.particleDataN4 as unknown as Particle[];
}

export async function loadGrammarN3(): Promise<GrammarPattern[]> {
  const module = await import('../../assets/js/core/grammarN3.js');
  return module.grammarPatterns as unknown as GrammarPattern[];
}

// ============================================
// Level Loader Factory
// ============================================

const levelLoaders: Record<JLPTLevel, () => Promise<Word[]>> = {
  N5: loadVocabularyN5,
  N4: loadVocabularyN4,
  N3: loadVocabularyN3,
  Everyday: loadVocabularyEveryday,
  Hiragana: loadHiragana,
  Katakana: loadKatakana
};

export async function loadVocabularyForLevel(level: JLPTLevel): Promise<Word[]> {
  const loader = levelLoaders[level];
  if (!loader) {
    console.warn(`Unknown level: ${level}, defaulting to N5`);
    return loadVocabularyN5();
  }
  return loader();
}

// ============================================
// Particle Loader by Level
// ============================================

export async function loadParticlesForLevel(level: JLPTLevel): Promise<Particle[]> {
  switch (level) {
    case 'N5':
      return loadParticlesN5();
    case 'N4':
      return loadParticlesN4();
    // N3 uses grammar patterns (loadGrammarForLevel); Everyday and the kana
    // levels have no particle/grammar mode. No wrong-level fallback content.
    default:
      return [];
  }
}

// ============================================
// Grammar Loader by Level
// ============================================

export async function loadGrammarForLevel(level: JLPTLevel): Promise<GrammarPattern[]> {
  return level === 'N3' ? loadGrammarN3() : [];
}

// ============================================
// Level Configuration
// ============================================

export const LEVEL_CONFIG: Record<JLPTLevel, Omit<LevelConfig, 'vocabulary'>> = {
  N5: {
    level: 'N5',
    displayName: 'JLPT N5',
    description: 'Beginner-level Japanese vocabulary',
    color: '#6F9B8E',
    enabled: true
  },
  N4: {
    level: 'N4',
    displayName: 'JLPT N4',
    description: 'Elementary-level Japanese vocabulary',
    color: '#C9A24A',
    enabled: true
  },
  N3: {
    level: 'N3',
    displayName: 'JLPT N3',
    description: 'Intermediate-level Japanese vocabulary',
    color: '#2D4A66',
    enabled: true
  },
  Everyday: {
    level: 'Everyday',
    displayName: '日常 Everyday',
    description: 'Commonly used Japanese words for daily life',
    color: '#9A4848',
    enabled: true
  },
  Hiragana: {
    level: 'Hiragana',
    displayName: 'ひらがな Hiragana',
    description: 'The basic Japanese phonetic script',
    color: '#A8C0CC',
    enabled: true
  },
  Katakana: {
    level: 'Katakana',
    displayName: 'カタカナ Katakana',
    description: 'The Japanese script for foreign words',
    color: '#5C6678',
    enabled: true
  }
};

// ============================================
// All Levels Info
// ============================================

export function getAvailableLevels() {
  return Object.values(LEVEL_CONFIG).filter(l => l.enabled);
}

export function getLevelConfig(level: JLPTLevel) {
  return LEVEL_CONFIG[level] || LEVEL_CONFIG.N5;
}
